import React, { useEffect, useRef, FC, RefObject, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { socketNotifications } from 'requests/socket'
import { fetchFeNotificationsFromOtk, markAllNotificationsRead, markNotificationRead, markOrderNotificationsRead } from 'requests/notifications'
import { Icon, IconType } from 'components/atoms/Icon'

import { selectBellNotifications } from 'store/notifications/notifications.selectors'
import { notificationsUpdateSuccess } from 'store/notifications/notifications.actions'
import { selectToken } from 'store/users/users.selectors'
import { Notification, isChatNotification, isDocumentUploadedNotification, documentUploadedHref, documentUploadedTitle } from 'types/notifications'
import { isNotificationsModuleEnabled } from 'utils/is-notifications-module-enabled'
import styles from './style.module.css'

interface NotificationsDropdownProps {
  isOpen: boolean
  onClose: () => void
}

const notificationRowKey = (notification: Notification, index: number): string =>
  notification.id || `${notification.type || 'chat'}-${notification.orderId}-${notification.createdAt}-${index}`

const notificationHref = (notification: Notification): string => {
  if (isDocumentUploadedNotification(notification)) {
    return documentUploadedHref(notification.orderId)
  }
  return `/order/${notification.orderId}/chat`
}

const notificationText = (notification: Notification): string => {
  if (isDocumentUploadedNotification(notification)) {
    const title = documentUploadedTitle(notification.orderId)
    return notification.fileName ? `${title}: ${notification.fileName}` : title
  }
  return `У вас новое сообщение по заявке ${notification.orderId}`
}

export const NotificationsDropdown: FC<NotificationsDropdownProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const notifications = useSelector(selectBellNotifications)
  const token = useSelector(selectToken)
  const dropdownRef: RefObject<HTMLDivElement> = useRef(null)
  const moduleEnabled = isNotificationsModuleEnabled()

  const syncFromOtk = useCallback(async () => {
    if (!token) return
    const list = await fetchFeNotificationsFromOtk(token)
    dispatch(notificationsUpdateSuccess(list))
  }, [dispatch, token])

  const handleRemoveNotification = (notification: Notification) => {
    if (moduleEnabled && token) {
      let clearPromise: Promise<unknown> = Promise.resolve()
      if (isDocumentUploadedNotification(notification) && notification.id) {
        clearPromise = markNotificationRead(token, notification.id)
      } else if (isChatNotification(notification)) {
        clearPromise = markOrderNotificationsRead(token, notification.orderId)
      } else if (notification.id) {
        clearPromise = markNotificationRead(token, notification.id)
      }

      clearPromise.then(() => syncFromOtk()).catch((err) => console.error('mark notification failed', err))
      return
    }
    socketNotifications.emit('reset', notification.orderId)
  }

  const handleRemoveAllNotifications = () => {
    if (moduleEnabled && token) {
      markAllNotificationsRead(token)
        .then(() => syncFromOtk())
        .catch((err) => console.error('mark all notifications failed', err))
      return
    }
    socketNotifications.emit('resetAll')
  }

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('click', handleClickOutside)
    } else {
      window.removeEventListener('click', handleClickOutside)
    }
    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [handleClickOutside, isOpen])

  return (
    <div className={styles.dropdown}>
      {isOpen && (
        <div className={styles.dropdownContent} ref={dropdownRef}>
          {/* eslint-disable-next-line react/button-has-type */}
          <button
            onClick={handleRemoveAllNotifications}
            className={notifications.length === 0 ? styles.disabled : styles.clearAllBtn}
            disabled={notifications.length === 0}
          >
            Очистить уведомления
          </button>
          {notifications.length === 0 ? (
            <p style={{ textAlign: 'center', marginTop: '8px' }}>У вас нет новых уведомлений</p>
          ) : (
            <ul className="p-0 m-0">
              {notifications.map((notification: Notification, index: number) => (
                <Link key={notificationRowKey(notification, index)} to={notificationHref(notification)} className={styles.link}>
                  <li className={styles.notificationItem} style={{ listStyleType: 'none' }}>
                    <div style={{ paddingRight: '26px' }}>{notificationText(notification)}</div>
                    <div style={{ color: '#abacad', fontSize: '12px' }}>{new Date(notification.createdAt).toLocaleString()}</div>
                    {/* eslint-disable-next-line react/button-has-type */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleRemoveNotification(notification)
                      }}
                      className={styles.removeBtn}
                      style={{ display: 'flex', padding: 0 }}
                    >
                      <Icon type={IconType.Close} width="16px" height="16px" />
                    </button>
                  </li>
                </Link>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
